import { useState, useEffect, useRef } from 'react';
import { SettingsLayout } from './SettingsLayout';
import { GradientBorder } from '../../components/ui/gradient-border';
import { Button } from '../../components/ui/button';
import { PulsingDot } from '../../components/ui/pulsing-dot';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { useSubscription } from '../../hooks/useSubscription';
import { useUsageHistory } from '../../hooks/useUsageHistory';
import { MessageSquare } from 'lucide-react';
import { getBarColor, getPlanBadgeStyle } from '../../lib/usage';
import type { TimelineItem, DailyUsageEntry } from '../../hooks/useUsageHistory';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return date
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase();
}

function formatActivityTime(item: TimelineItem): string {
  if (
    item.type !== 'request' ||
    item.usageScope !== 'conversation' ||
    !item.startedAt ||
    item.startedAt === item.timestamp
  ) {
    return formatTimestamp(item.timestamp);
  }
  const start = new Date(item.startedAt);
  const end = new Date(item.timestamp);
  if (toLocalDateStr(start) !== toLocalDateStr(end)) {
    const formatDateTime = (date: Date) =>
      `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${formatTimestamp(date.toISOString())}`;
    return `${formatDateTime(start)}–${formatDateTime(end)}`;
  }
  return `${formatTimestamp(item.startedAt)}–${formatTimestamp(item.timestamp)}`;
}

function formatPeriod(start: string | null, end: string | null): string {
  if (!start || !end) return '';
  const s = new Date(start);
  const e = new Date(end);
  const startStr = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startStr} \u2013 ${endStr}`;
}

function formatCredits(credits: number): string {
  return credits.toLocaleString('en-US');
}

function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateGroup(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const today = toLocalDateStr(now);
  const yesterdayDate = new Date(now);
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = toLocalDateStr(yesterdayDate);
  const dateStr = toLocalDateStr(date);

  if (dateStr === today) return 'Today';
  if (dateStr === yesterday) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Section 1: Credit Summary
// ---------------------------------------------------------------------------

function CreditSummary() {
  const {
    isLoading: balanceLoading,
    monthlyRemaining,
    periodAllowance,
    topUpCredits,
    usagePercent,
    periodEnd,
  } = useCreditBalance();
  const { planType, plans, subscription, isLoading: subLoading } = useSubscription();

  const isLoading = balanceLoading || subLoading;
  if (isLoading) return null;

  const currentPlan = plans.find((p) => p.type === planType);
  const isFree = planType === 'free';
  const isExhausted = usagePercent >= 95;
  const hasMonthlyCredits = periodAllowance > 0;

  const renewalLabel = (() => {
    if (!currentPlan || !periodEnd) return '';
    const endDate = new Date(periodEnd);
    const daysLeft = Math.max(
      0,
      Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
    );
    if (subscription?.cancelAtPeriodEnd) {
      if (daysLeft === 0) return 'Cancels today';
      return daysLeft <= 30
        ? `Cancels in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`
        : `Cancels ${endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
    }
    const nextCredits = currentPlan.creditsPerMonth.toLocaleString();
    if (daysLeft === 0) return `Renews today, credits renew at ${nextCredits}`;
    return daysLeft <= 30
      ? `Renews in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}, credits renew at ${nextCredits}`
      : `Renews ${endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
  })();

  if (isExhausted) {
    let exhaustedMessage: string;
    if (isFree && topUpCredits > 0) {
      exhaustedMessage = "You've used all your free credits, but you still have top-up credits.";
    } else if (isFree) {
      exhaustedMessage = "You've used all your free credits. Upgrade to keep building.";
    } else if (topUpCredits > 0) {
      exhaustedMessage = 'Monthly credits used. Drawing from your top-up balance.';
    } else {
      exhaustedMessage = 'Monthly credits exhausted. Top up to keep building.';
    }

    const renewalDate = periodEnd
      ? new Date(periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
      : null;

    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/[0.04] p-5 space-y-3">
        <h2 className="text-lg font-semibold text-white">{exhaustedMessage}</h2>
        <div className="space-y-1.5">
          <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-red-500 w-full" />
          </div>
          <p className="text-xs text-white/50">
            0 of {periodAllowance.toLocaleString()}{' '}
            {isFree ? 'free credits' : 'monthly credits'} remaining
            {renewalDate && `. Resets ${renewalDate}`}
          </p>
        </div>
        {topUpCredits > 0 && (
          <div className="flex items-center justify-between border-t border-white/10 pt-3">
            <p className="text-xs text-white inline-flex items-center gap-1.5">
              Top-up balance
              <span className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full translate-y-px">
                Active
              </span>
            </p>
            <p className="text-sm font-medium text-white tabular-nums">
              {topUpCredits.toLocaleString()} credits
            </p>
          </div>
        )}
        {!topUpCredits && (
          <div className="border-t border-white/10 pt-3">
            <a href="/settings/plans" className="text-xs text-white/50 hover:text-white/70 transition-colors">
              {isFree ? (
                <>Upgrade to keep building. <span className="underline">View plans</span></>
              ) : (
                <><span className="underline">Top up credits</span> to keep building.</>
              )}
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <GradientBorder innerClassName="p-6 space-y-4">
      <div>
        <span
          className={`-ml-1 inline-flex items-center rounded-full px-3 py-1 text-lg font-semibold uppercase tracking-wider ${getPlanBadgeStyle(planType)}`}
        >
          {isFree ? 'Free' : currentPlan?.name ?? planType}
        </span>
        <p className="text-xs text-white/50 mt-2">
          {renewalLabel || 'Monthly allowance'}
        </p>
      </div>

      {isFree ? (
        <div className="space-y-4">
          {hasMonthlyCredits && (
            <div className="space-y-1.5">
              <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${getBarColor(usagePercent)}`}
                  style={{ width: `${Math.min(usagePercent, 100)}%` }}
                />
              </div>
              <p className="text-xs text-white/50 tabular-nums">
                {monthlyRemaining.toLocaleString()} of{' '}
                {periodAllowance.toLocaleString()} credits remaining
              </p>
            </div>
          )}
          {topUpCredits > 0 && (
            <div
              className={`flex items-center justify-between ${hasMonthlyCredits ? 'border-t border-white/5 pt-3' : ''}`}
            >
              <p className="text-xs text-white">Top-up balance</p>
              <p className="text-sm font-medium text-white tabular-nums">
                {topUpCredits.toLocaleString()} credits
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-medium text-white">Monthly credits</h4>
              <span className="text-xs text-white tabular-nums">
                {usagePercent}% used
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${getBarColor(usagePercent)}`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
            <p className="text-xs text-white/50 tabular-nums">
              {monthlyRemaining.toLocaleString()} of{' '}
              {periodAllowance.toLocaleString()} remaining
            </p>
          </div>
          {topUpCredits > 0 && (
            <div className="flex items-center justify-between border-t border-white/5 pt-3">
              <p className="text-xs text-white inline-flex items-center gap-1.5">
                Top-up balance
                {monthlyRemaining <= 0 ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full translate-y-px">
                    Active
                  </span>
                ) : (
                  <span className="text-white/50">
                    &middot; used after monthly credits
                  </span>
                )}
              </p>
              <p className="text-sm font-medium text-white tabular-nums">
                {topUpCredits.toLocaleString()} credits
              </p>
            </div>
          )}
        </div>
      )}

      {/* Contextual CTAs */}
      {isFree && (
        <div className="border-t border-white/5 pt-3">
          <a href="/settings/plans" className="text-xs text-white/40 hover:text-white/60 transition-colors">
            Need more credits? View plans &rarr;
          </a>
        </div>
      )}
      {!isFree && usagePercent >= 80 && topUpCredits === 0 && (
        <div className="border-t border-white/5 pt-3">
          <a href="/settings/plans" className="text-xs text-amber-400/80 hover:text-amber-400 transition-colors">
            You're running low on credits. <span className="underline">Top up</span>
          </a>
        </div>
      )}
    </GradientBorder>
  );
}

// ---------------------------------------------------------------------------
// Section 2: Daily Usage Chart
// ---------------------------------------------------------------------------

function DailyUsageChart({
  data,
}: {
  data: DailyUsageEntry[];
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  if (data.length === 0) return null;

  const now = new Date();
  const today = toLocalDateStr(now);

  // Only show days up to today, not the full period
  const visibleData = data.filter((d) => d.date <= today);

  if (visibleData.length === 0) return null;

  const maxCredits = Math.max(...visibleData.map((d) => d.credits));
  const barCount = visibleData.length;
  const chartWidth = 600;
  const chartHeight = 150;
  const gap = Math.max(1, Math.min(4, Math.floor(chartWidth / barCount / 5)));
  const barWidth = Math.max(4, (chartWidth - gap * (barCount - 1)) / barCount);

  const yMax = maxCredits || 1;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className="w-full"
        style={{ height: 150 }}
        preserveAspectRatio="none"
      >
        {visibleData.map((entry, i) => {
          const x = i * (barWidth + gap);
          const barHeight =
            entry.credits > 0
              ? Math.max(3, (entry.credits / yMax) * (chartHeight - 4))
              : 2;
          const y = chartHeight - barHeight;
          const isHovered = hoveredIdx === i;
          return (
            <g
              key={entry.date}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* Invisible hit target for easier hovering */}
              <rect
                x={x}
                y={0}
                width={barWidth}
                height={chartHeight}
                fill="transparent"
              />
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx={Math.min(3, barWidth / 2)}
                className={
                  entry.credits > 0
                    ? isHovered
                      ? 'fill-white/45'
                      : 'fill-white/25'
                    : 'fill-white/8'
                }
              />
            </g>
          );
        })}
      </svg>
      {/* HTML tooltip overlay — always rendered, fade via opacity */}
      {visibleData.map((entry, i) => {
        const barCenterPct = ((i * (barWidth + gap) + barWidth / 2) / chartWidth) * 100;
        const barHeightPx = entry.credits > 0 ? Math.max(3, (entry.credits / yMax) * (150 - 4)) : 2;
        const topPx = 150 - barHeightPx - 8; // 8px gap above bar
        const isVisible = hoveredIdx === i;
        return (
          <div
            key={entry.date}
            className="absolute pointer-events-none transition-opacity duration-150"
            style={{
              left: `${barCenterPct}%`,
              top: topPx,
              transform: 'translateX(-50%) translateY(-100%)',
              opacity: isVisible ? 1 : 0,
            }}
          >
            <div className="bg-[#1a1a1a] border border-white/10 rounded-md px-2.5 py-1 text-[12px] text-white/90 whitespace-nowrap shadow-lg">
              {new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, {formatCredits(entry.credits)} credits
            </div>
            {/* Arrow with border */}
            <div className="flex justify-center -mt-px">
              <svg width="12" height="7" viewBox="0 0 12 7">
                <path d="M0 0 L6 7 L12 0" fill="rgba(255,255,255,0.1)" />
                <path d="M1 0 L6 5.5 L11 0" fill="#1a1a1a" />
              </svg>
            </div>
          </div>
        );
      })}
      <div className="flex justify-between mt-1.5">
        <span className="text-[10px] text-white/50">
          {new Date(visibleData[0].date + 'T00:00:00').toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })}
        </span>
        <span className="text-[10px] text-white/50">
          {new Date(
            visibleData[visibleData.length - 1].date + 'T00:00:00',
          ).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3: Activity Timeline
// ---------------------------------------------------------------------------

function TimelineItemRow({ item }: { item: TimelineItem }) {
  if (item.type === 'credit') {
    return (
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-4">
          <span className="text-xs text-white/50 shrink-0">
            {formatTimestamp(item.timestamp)}
          </span>
          <span className="text-sm font-medium text-emerald-400 tabular-nums shrink-0">
            +{formatCredits(item.credits)} credits
          </span>
        </div>
        <p className="text-sm text-white/70 mt-1">{item.description}</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 flex gap-4">
      <div className="flex-1 min-w-0">
        {item.usageScope === 'conversation' ? (
          <p className="text-sm text-white/70">Conversation activity</p>
        ) : item.summary ? (
          <p className="text-sm text-white/70">{item.summary}</p>
        ) : (
          <p className="text-sm text-white/70 truncate italic">
            {item.messageText}
          </p>
        )}
        {(item.usageScope === 'request' || item.conversationTitle) && (
          <p className="text-[14px] text-white/40 mt-0.5 flex items-center gap-1.5 italic">
            <MessageSquare className="h-3 w-3 shrink-0" />
            <span className="truncate">{item.messageText}</span>
          </p>
        )}
        <div className="flex items-center gap-1.5 text-[14px] text-white/40 mt-1.5">
          <span className="shrink-0">{formatActivityTime(item)}</span>
          <span className="text-white/20">&middot;</span>
          <span className="truncate">{item.siteName}</span>
        </div>
      </div>
      <div className="shrink-0 text-right w-32">
        <p className="text-sm font-medium text-white tabular-nums">
          -{formatCredits(item.credits)} Credits
        </p>
        {(item.stepCount > 0 || item.imageCount > 0) && (
          <p className="text-[14px] text-white/50 tabular-nums mt-0.5">
            {[
              item.stepCount > 0 &&
                `${item.stepCount} ${item.stepCount === 1 ? 'Step' : 'Steps'}`,
              item.imageCount > 0 &&
                `${item.imageCount} ${item.imageCount === 1 ? 'Image' : 'Images'}`,
            ]
              .filter(Boolean)
              .join(' \u00b7 ')}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SettingsUsagePage() {
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<TimelineItem[]>([]);
  const lastPageMerged = useRef(0);
  const { data, isLoading } = useUsageHistory(page);
  const { planType } = useSubscription();

  // Append new page data when it arrives
  useEffect(() => {
    if (data?.items && page > lastPageMerged.current) {
      lastPageMerged.current = page;
      if (page === 1) {
        setAccumulated(data.items);
      } else {
        setAccumulated((prev) => [...prev, ...data.items]);
      }
    }
  }, [data, page]);

  const periodLabel = (() => {
    if (planType === 'free') return 'Last 30 days';
    if (data?.period) {
      const range = formatPeriod(data.period.start, data.period.end);
      return range ? `Current billing period: ${range}` : '';
    }
    return '';
  })();
  const hasMore = data?.hasMore ?? false;
  const dailyUsage = data?.dailyUsage ?? [];

  // Group items by date for section headers
  const groupedItems: Array<{ dateLabel: string; items: Array<{ item: TimelineItem; idx: number }> }> = [];
  let currentGroup: (typeof groupedItems)[number] | null = null;

  accumulated.forEach((item, idx) => {
    const dateLabel = formatDateGroup(item.timestamp);
    if (!currentGroup || currentGroup.dateLabel !== dateLabel) {
      currentGroup = { dateLabel, items: [] };
      groupedItems.push(currentGroup);
    }
    currentGroup.items.push({ item, idx });
  });

  return (
    <SettingsLayout activeTab="usage">
      <div className="space-y-10">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-semibold text-white">Usage</h1>
        </div>

        {/* Section 1: Credit Summary */}
        <CreditSummary />

        {isLoading && accumulated.length === 0 ? (
          <div className="flex justify-center py-16">
            <PulsingDot />
          </div>
        ) : accumulated.length === 0 && dailyUsage.length === 0 ? (
          <GradientBorder innerClassName="w-full py-10 flex flex-col items-center">
            <p className="text-white/50 text-sm">No usage this billing period</p>
          </GradientBorder>
        ) : (
          <div>
            <h2 className="text-lg font-semibold text-white">Activity</h2>
            {periodLabel && (
              <p className="text-sm text-white/50 mt-1 mb-4">{periodLabel}</p>
            )}

            {dailyUsage.length > 0 && (
              <div className="mb-6">
                <DailyUsageChart data={dailyUsage} />
              </div>
            )}
            {groupedItems.map((group) => (
              <div key={group.dateLabel} className="mt-8 first:mt-0 mb-6">
                <p className="text-[14px] font-medium text-white/50 uppercase tracking-wider mb-2">
                  {group.dateLabel}
                </p>
                <GradientBorder innerClassName="divide-y divide-white/5">
                  {group.items.map(({ item, idx }) => (
                    <TimelineItemRow
                      key={`${item.timestamp}-${idx}`}
                      item={item}
                    />
                  ))}
                </GradientBorder>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-center py-4">
                <PulsingDot />
              </div>
            )}
          </div>
        )}

        {hasMore && !isLoading && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => setPage((p) => p + 1)}>
              Load more
            </Button>
          </div>
        )}
      </div>
    </SettingsLayout>
  );
}
